import { connect, ConnectedProps } from 'react-redux'
import SellReadOnlyHeaderAccounts from '../../../../../headers/SellReadOnlyHeaderAccounts'
import SellReadOnlyHeaderCosts from '../../../../../headers/SellReadOnlyHeaderCosts'
import SellReadOnlyHeaderFinancialRows from '../../../../../headers/SellReadOnlyHeaderFinancialRows'
import SellReadOnlyHeaderMain, { useSellReadOnlyHeaderMainLogic } from '../../../../../headers/SellReadOnlyHeaderMain'
import SellReadOnlyHeaderShipping from '../../../../../headers/SellReadOnlyHeaderShipping'
import InputTableLeftColumn, { InputTableLeftColumnCompleteRequiredProps } from '../../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn'
import withLoading from '../../../../../../../../../../../utils/hoc/withLoading'
import useSellDeliveryNoteHeaderUrls from '../../../../../../../../../../../utils/hooks/sells/orders/useSellDeliveryNoteHeaderUrls'
import { ComponentTabObject } from '../../../../../../../../../../../utils/hooks/useTabLogic'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../../../../../../utils/reducers'
import { CostSellPayload } from '../../../../../../../../../../../utils/reducers/company/costsSell'
import { SellTagsPayload } from '../../../../../../../../../../../utils/reducers/sells/sellTags'
import { SellVariablePayload } from '../../../../../../../../../../../utils/reducers/company/sellVariable'
import SellStepHeaderVariables from '../../../../../headers/SellStepHeaderVariables'
import AppStyleDecider from '../../../../../../../../../../../baseComponents/AppsStyleDecider'
import InputTableTopHeader from '../../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableTopHeader'
import ITHeaderText, { ITHeaderTextCustomer } from '../../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import { useOrderITHeaderHandleChangeLogic } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/useOrderITHeaderHandleChange'
import { FormElement, FormRender } from '../../../../../../../../../../../forms/FormRenderer/types'
import { SellModelType } from '../../../../../headers/types'
import { displayDate } from '../../../../../../../../../../../utils/globals/date'
import { InputWithLabelMargin } from '../../../../../../../../../../../forms/Input'
import { ITHeaderOrderTagsNew } from '../../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components'
import { CustomSellSelectProject, CustomSellSelectCampaign } from '../../../../../../../../../../../specificComponents/sells/components'
import { returnClassOfSpanTemplate } from '../../../../../../../../../../../utils/globals/tailwind'
import { ProductTypesPayload } from '../../../../../../../../../../../utils/reducers/products/productTypes'


type SellDeliveryNoteWaitingResponseITHeaderProps = InputTableLeftColumnCompleteRequiredProps & ReduxSellDeliveryNoteWaitingResponseITHeader & {}

const SellDeliveryNoteWaitingResponseITHeader: React.FC<SellDeliveryNoteWaitingResponseITHeaderProps> = ({ ...rest }) => {
    return <AppStyleDecider DefaultComponent={SellDeliveryNoteWaitingResponseITHeaderDefault} NewComponent={SellDeliveryNoteWaitingResponseITHeaderNew} {...rest} />
}

const SellDeliveryNoteWaitingResponseITHeaderDefault: React.FC<SellDeliveryNoteWaitingResponseITHeaderProps> = ({ sellTags, ...rest }) => {
    const { t } = useTwinTranslation()
    const orderHeaderURLs = useSellDeliveryNoteHeaderUrls()

    const tabs: ComponentTabObject = {
        'main': {
            component: SellReadOnlyHeaderMain,
            text: t('dataDeliveryNote', 'Datos del albarán'),
            extraProps: { orderHeaderURLs, sellTags: sellTags?.sell }
        },
        'financialRow': {
            component: SellReadOnlyHeaderFinancialRows,
            text: t('discountsAndRecharges', 'Descuentos y recargos'),
            permission: 'sell.deliveryNote',
            extraProps: { orderHeaderURLs }
        },
        'costs': {
            component: SellReadOnlyHeaderCosts,
            text: t('costsDeliveryNote', 'Gastos del albarán'),
            permission: 'sell.deliveryNote',
            extraProps: { orderHeaderURLs }
        },
        'shipping': {
            component: SellReadOnlyHeaderShipping,
            text: t('shipping', 'Transporte'),
            permission: 'sell.deliveryNote',
            extraProps: { orderHeaderURLs }
        },
        'accounts': {
            component: SellReadOnlyHeaderAccounts,
            text: t('divideIntoChapters', 'Dividir en capítulos'),
            permission: 'sell.deliveryNote',
            extraProps: { orderHeaderURLs }
        },
        'variables': {
            component: SellStepHeaderVariables,
            text: t('sellVariables', 'Variables de venta'),
            permission: 'sell.deliveryNote',
            extraProps: { orderHeaderURLs }
        },
    }
    return (
        <InputTableLeftColumn tabs={tabs} {...rest} />
    )
}

const SellDeliveryNoteWaitingResponseITHeaderNew: React.FC<SellDeliveryNoteWaitingResponseITHeaderProps> = ({ sellTags, setCompleteData, headerData, updateDataURL, getCompleteData }) => {
    const { t } = useTwinTranslation()
    const { handleChange } = useOrderITHeaderHandleChangeLogic({ setCompleteData, updateDataURL })
    const { memorizedAddresses, activeCurrencies, customerProjects, customerCampaigns, getCustomerCampaigns, getCustomerProjects } = useSellReadOnlyHeaderMainLogic({ headerData: headerData || {}, setCompleteData, handleChange })
    let extraFields: FormElement<keyof SellModelType>[] = []
    if (headerData?.CustomerAddressId) {
        extraFields.push({
            name: 'CustomerAddressId',
            component: 'Custom',
            render: ({ value }) => {
                return <ITHeaderText label={t('fiscalAddress', 'Dirección Fiscal')} value={memorizedAddresses.customerAddress} />
            },
            extraProps: {
                customerAddress: memorizedAddresses.customerAddress
            },
            hideSectionByDefault: true
        })
    }
    const sections: FormRender<SellModelType> = [
        {
            cols: 7,
            className: 'gap-x-20',
            elements: [
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('assignedN', 'Nº asignado')} value={value} className={(headerData?.assigned_number ? '' : 'text-red-BA')} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('date', 'Fecha')} value={displayDate(new Date(value))} />
                },
                {
                    name: 'RepresentativeEmployeeId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('representative', 'Representante')} value={headerData?.RepresentativeEmployee?.fullname_short} />
                },
                {
                    name: 'custom_name',
                    component: 'Custom',
                    render: (props) => {
                        return <InputWithLabelMargin name='custom_name' label={t('customName', 'Nombre personalizado')} onChange={props.onChange} defaultValue={headerData?.custom_name} className='custom_name_input_field' />
                    }
                },
                {
                    name: 'CustomerProjectId',
                    component: 'Custom',
                    render: ({ extraProps, setLockOnClickOut }) => <CustomSellSelectProject getCompleteData={getCustomerProjects} handleChange={handleChange} headerData={headerData || {}} items={extraProps.customerProjects} setLockOnClickOut={setLockOnClickOut} />,
                    extraProps: {
                        customerProjects: customerProjects || {}
                    }
                },
                {
                    name: 'CampaignCustomerId',
                    component: 'Custom',
                    render: ({ extraProps, setLockOnClickOut }) => <CustomSellSelectCampaign getCompleteData={getCustomerCampaigns} handleChange={handleChange} headerData={headerData || {}} items={extraProps.customerCampaigns} label={t('campaign', 'Campaña')} setLockOnClickOut={setLockOnClickOut} />,
                    extraProps: {
                        customerCampaigns: customerCampaigns || {}
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'CustomerId',
                    component: 'Custom',
                    render: () => <ITHeaderTextCustomer label={t('customer', 'Cliente')} headerData={headerData || {}} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'OrderTags',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return (
                            <ITHeaderOrderTagsNew tagType={1} getCompleteData={getCompleteData} dictTags={extraProps?.dictTags || []} className={returnClassOfSpanTemplate(3) + ' order_top_header_custom_select_tags'} selectedTags={headerData?.tags || []} onChange={handleChange} headerData={headerData || {}} />)
                    },
                    extraProps: {
                        dictTags: sellTags?.sell
                    }
                },
                ...extraFields,
                {
                    name: 'WorkingCenterAddressId',
                    component: 'Custom',
                    render: ({ value, extraProps }) => {
                        return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={memorizedAddresses.workingCenterAddress} />
                    },
                    extraProps: {
                        workingCenterAddress: memorizedAddresses.workingCenterAddress
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('soldBy', 'Vendido por')} value={headerData?.WorkingCenter.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('billingBy', 'Facturado por')} value={headerData?.BillingWorkingCenter?.name || ''} />
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie || ''} />
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('currency', 'Moneda')} value={activeCurrencies?.[value]?.name} />,
                    hideSectionByDefault: true
                },
            ]
        }
    ]
    return (
        <InputTableTopHeader handleChange={handleChange} sections={sections} headerData={headerData} getCompleteData={getCompleteData} />
    )
}

const SellDeliveryNoteWaitingResponseITHeaderDispatch = {
    setCostsSell: (payload: CostSellPayload) => ({ type: 'CHANGE_COSTSELL', payload }),
    setSellTags: (payload: SellTagsPayload) => ({ type: 'CHANGE_SELL_TAGS', payload }),
    setSellVariables: (payload: SellVariablePayload) => ({ type: 'CHANGE_SELLVARIABLE', payload }),
    setProductTypes: (payload: ProductTypesPayload) => ({ type: 'CHANGE_PRODUCT_TYPES', payload }),
}

export type ReduxSellDeliveryNoteWaitingResponseITHeader = ConnectedProps<typeof sellDeliveryNoteWaitingResponseITHeaderConnect>
const mapSellDeliveryNoteWaitingResponseITHeaderConnector = (state: AllReduxPayloads) => ({ costsSell: state.costsSell, sellTags: state.sellTags, sellVariables: state.sellVariables, productTypes: state.productTypes })
const sellDeliveryNoteWaitingResponseITHeaderConnect = connect(mapSellDeliveryNoteWaitingResponseITHeaderConnector, SellDeliveryNoteWaitingResponseITHeaderDispatch)
const SellDeliveryNoteWaitingResponseITHeaderConnectLoading = withLoading(SellDeliveryNoteWaitingResponseITHeader, [{ fetchUrl: '/api/app/config/cost/costSell/getAllCostSells', propName: 'costsSell', setFunctionName: 'setCostsSell' }, { fetchUrl: '/api/app/order/sell/sellTag/getAllSellTags', propName: 'sellTags', setFunctionName: 'setSellTags' }, { fetchUrl: '/api/app/config/sell/sellVariable/getAllSellVariables', propName: 'sellVariables', setFunctionName: 'setSellVariables' }, { fetchUrl: '/api/app/product/productType/getAllProductTypes', propName: 'productTypes', setFunctionName: 'setProductTypes' }])
const SellDeliveryNoteWaitingResponseITHeaderConnect = sellDeliveryNoteWaitingResponseITHeaderConnect(SellDeliveryNoteWaitingResponseITHeaderConnectLoading)

export default SellDeliveryNoteWaitingResponseITHeaderConnect
